import {Bar, Line} from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import {ChartFrame, EmptyState} from 'pages/home/shared/DashboardUI';
import {PALETTE, baseOptions} from 'pages/home/shared/chartTheme';
import {Explained} from './Info';
import {
  formatAverageRating,
  formatCount,
  formatCoverage,
  formatDuration,
} from './performanceUtils';
import styles from './performance.module.scss';

ChartJS.register(
  BarElement,
  CategoryScale,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
);

const bucketLabel = (iso, bucket) => {
  const date = new Date(iso);
  const day = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return bucket === 'WEEK' ? `Week of ${day}` : day;
};

const hasValue = value => value !== null && value !== undefined;

const xAxis = labels => ({
  type: 'category',
  labels,
  grid: {display: false},
  border: {color: PALETTE.line},
  ticks: {
    color: PALETTE.muted,
    font: {size: 11},
    maxRotation: 0,
    autoSkip: true,
    maxTicksLimit: 8,
  },
});

/**
 * Bigger dot, more ratings behind it: a 5★ day from one customer should not
 * look as weighty as a 4★ day from twenty.
 */
export const dotSize = count => Math.min(12, 4 + Math.sqrt(count || 0) * 2.5);

/**
 * Resolved per UTC day or week, and — as separate dots, never joined by a
 * line — the average rating and calendar resolution time on the days that
 * had any.
 *
 * Averages exist only on some days. Joining them drew stray strokes between
 * unrelated days and suggested a trend that wasn't there; a missing day is a
 * gap, never a zero.
 */
export const TrendCharts = ({trends, bucket, loading, metrics}) => {
  const points = trends || [];
  const labels = points.map(point => bucketLabel(point.from, bucket));
  const unit = bucket === 'WEEK' ? 'week' : 'day';
  const rated = points.filter(point => hasValue(point.averageRating));
  // Calendar resolution time, the same measure as the headline tile.
  const timed = points.filter(point =>
    hasValue(point.averageCustomerWaitSeconds),
  );

  if (loading && !points.length) {
    return <div className={styles.chartLoading}>Loading trends…</div>;
  }

  if (!points.length) {
    return (
      <EmptyState
        compact
        title="No trend for this period"
        description="Nothing was resolved in the selected range."
      />
    );
  }

  const resolvedOptions = baseOptions({integer: true, valueLabel: 'Resolved'});
  resolvedOptions.scales.x = xAxis(labels);
  resolvedOptions.scales.y = {
    ...resolvedOptions.scales.y,
    ticks: {...(resolvedOptions.scales.y?.ticks || {}), precision: 0},
  };

  const ratingOptions = baseOptions({valueLabel: 'Average rating'});
  ratingOptions.scales.x = xAxis(labels);
  ratingOptions.scales.y = {
    ...ratingOptions.scales.y,
    min: 0,
    // Room above 5★ so a perfect day isn't cut in half by the edge.
    max: 5.6,
    ticks: {
      ...(ratingOptions.scales.y?.ticks || {}),
      stepSize: 1,
      callback: value =>
        value <= 5 && Number.isInteger(value) ? `${value}★` : '',
    },
  };
  ratingOptions.plugins.tooltip.callbacks = {
    title: items => labels[items[0].dataIndex],
    label: context => {
      const point = points[context.dataIndex];
      return [
        ` Average ${formatAverageRating(point.averageRating)}`,
        ` From ${formatCount(point.ratingCount || 0)} of ${formatCount(
          point.resolved || 0,
        )} resolved tickets (${formatCoverage(point.ratingCoverage ?? 0)})`,
      ];
    },
  };

  const timeOptions = baseOptions({valueLabel: 'Resolution time'});
  timeOptions.scales.x = xAxis(labels);
  timeOptions.scales.y = {
    ...timeOptions.scales.y,
    ticks: {
      ...(timeOptions.scales.y?.ticks || {}),
      maxTicksLimit: 4,
      callback: value => formatDuration(value),
    },
  };
  timeOptions.plugins.tooltip.callbacks = {
    title: items => labels[items[0].dataIndex],
    label: context =>
      ` Average resolution time ${formatDuration(
        points[context.dataIndex].averageCustomerWaitSeconds,
      )}`,
  };

  // A line dataset with the line switched off: one dot per day that has a
  // value, aligned to the day labels; null days draw nothing.
  const dots = (values, color, sizes) => ({
    labels,
    datasets: [
      {
        data: values,
        spanGaps: false,
        backgroundColor: color,
        borderColor: '#fff',
        borderWidth: 1.5,
        pointRadius: sizes,
        pointHoverRadius: sizes.map(size => size + 2),
        showLine: false,
        clip: false,
      },
    ],
  });

  return (
    <div className={styles.trendGrid}>
      <div className={styles.trendCard}>
        <div className={styles.trendHead}>
          <h4>Resolved per {unit}</h4>
          <span className={styles.smallNote}>
            {formatCount(metrics?.resolved || 0)} in total
          </span>
        </div>
        <ChartFrame height={170}>
          <Bar
            data={{
              labels,
              datasets: [
                {
                  label: 'Resolved',
                  data: points.map(point => point.resolved || 0),
                  backgroundColor: PALETTE.brand,
                  borderRadius: 4,
                  maxBarThickness: 28,
                },
              ],
            }}
            options={resolvedOptions}
          />
        </ChartFrame>
      </div>

      <div className={styles.trendCard}>
        <div className={styles.trendHead}>
          <h4>
            <Explained text="Each dot is a day (or week) when customers left ratings. Its height is that day's average; a bigger dot means more ratings behind it. Days with no ratings have no dot.">
              Average rating per {unit}
            </Explained>
          </h4>
          {hasValue(metrics?.averageRating) && (
            <span className={styles.smallNote}>
              Overall{' '}
              <strong>{formatAverageRating(metrics.averageRating)}</strong> from{' '}
              {formatCount(metrics.ratingCount || 0)} rating
              {metrics.ratingCount === 1 ? '' : 's'} on {rated.length} {unit}
              {rated.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {rated.length ? (
          <ChartFrame height={170}>
            <Line
              data={dots(
                points.map(point =>
                  hasValue(point.averageRating) ? point.averageRating : null,
                ),
                '#d97706',
                points.map(point => dotSize(point.ratingCount)),
              )}
              options={ratingOptions}
            />
          </ChartFrame>
        ) : (
          <EmptyState
            compact
            title="No ratings in this period"
            description="Customers didn't rate any resolved ticket in these dates."
          />
        )}
      </div>

      <div className={styles.trendCard}>
        <div className={styles.trendHead}>
          <h4>
            <Explained text="Each dot is a day (or week) when tickets were resolved: how long, on average, from the customer opening them. Clock time — nights and weekends count. Days with none have no dot.">
              Resolution time per {unit}
            </Explained>
          </h4>
          {hasValue(metrics?.averageCustomerWaitSeconds) && (
            <span className={styles.smallNote}>
              Overall average{' '}
              <strong>
                {formatDuration(metrics.averageCustomerWaitSeconds)}
              </strong>
            </span>
          )}
        </div>
        {timed.length ? (
          <ChartFrame height={170}>
            <Line
              data={dots(
                points.map(point =>
                  hasValue(point.averageCustomerWaitSeconds)
                    ? point.averageCustomerWaitSeconds
                    : null,
                ),
                PALETTE.brand,
                points.map(() => 6),
              )}
              options={timeOptions}
            />
          </ChartFrame>
        ) : (
          <EmptyState
            compact
            title="Not available"
            description="No ticket resolved in these dates has a measured resolution time."
          />
        )}
      </div>
    </div>
  );
};
